import { Draggable } from '@hello-pangea/dnd';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Message, Popover } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core';
import { type EventEmitter } from '@meitu/saas-editor-core/es/framework/services/events';
import {
    CanvasType,
    IExtraInfo,
    ILayerProps,
    MAX_STAGES,
    MAX_STAGES_TIP,
    PosterType,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import useClickOutside from '@/hooks/use-click-outside';
import { useLocaleStore } from '@/i18n';
import { checkTypeIsAiPPT, checkTypeIsPPT } from '@/utils/stage';
import { IS_DRAGGING_EVENT, NAV_SCROLL_EVENT, NAV_SCROLL_FRAME_EVENT } from '../../event-name';
import { useNavigationCollapseStore } from '../../store/navigation-collapse-store';
import AIPPTComponent from '../ai-ppt-component';
import { useMultiSelect } from '../multi-select-provider';
import { RegenAiPPT } from '../regen-ai-ppt';
import './stage.less';
import { type QuickStageOperate } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/stage-operate';
import { type FontItem } from '@meitu/xdesign-widgets';
import { RenderStagePreview } from './stage-preview';

export function getScale(wrapWidth: number, width: number, height: number) {
    const scale = wrapWidth / width;
    return {
        scale,
    };
}

const CONTENT_SIZE = 168;

interface IStageItem {
    editor: Editor;
    events: EventEmitter;
    stageIndex: number;
    originIndex: number;
    selectedIndexes: number[];
    activeIndex: number;
    recordId: string;
    setIsAiCreate: (isAiCreate: boolean) => void;
    setAiRegen: (data: { isAiRegen: boolean; regenIndex?: number }) => void;
    checkIsCover: (type: any) => boolean;
    closeAiPPTPrompt: () => void;
    canvasType: CanvasType;
    /** 海报保存类型 */
    posterType: PosterType;
    stageExtraInfo?: { [key: string]: any };
    transferTemplateToClient: (templateDetail: any, fontList: FontItem[]) => Promise<any>;
    packageTemplateConf: (
        templateConf: any,
        extraInfo: IExtraInfo,
        init: boolean,
    ) => {
        stages: ILayerProps[][];
        extraInfos: IExtraInfo[];
    };
    fetchPPTApplyItem: (params: any) => Promise<any>;
    fetchPPTCheckItemProcess: (params: any) => Promise<any>;
    fetchPPTItemDetail: (params: any) => Promise<any>;
    templateSubject: string;
    isAiRegen: boolean;
    initPPT: (stageIndex?: number) => void | Promise<void>;
    regenIndex: number;
    transferIdParams: any;
    isFromHuaweiCustom: () => boolean;
    getCurrentScene: () => string;
    isFullscreen: boolean;
    showCreateTip: (tip: string) => void;
    hideCreateTip: () => void;
    fetchAIPPTComponentList: (data: any) => Promise<any>;
    getStagePoints: (layers: ILayerProps[], page_type: string) => number;
    replaceComponentKit: (
        componentUrl: string,
        templateUrl: string,
        layers: ILayerProps[],
        fonts: any[],
        page_type: string,
        themeType: string,
    ) => Promise<any[]>;
}

export const StageItem: React.FC<IStageItem> = React.memo(
    ({
        stageIndex,
        originIndex,
        selectedIndexes,
        activeIndex,
        recordId,
        setIsAiCreate,
        setAiRegen,
        events,
        editor,
        checkIsCover,
        closeAiPPTPrompt,
        canvasType,
        posterType,
        stageExtraInfo,
        transferTemplateToClient,
        packageTemplateConf,
        fetchPPTApplyItem,
        fetchPPTCheckItemProcess,
        fetchPPTItemDetail,
        templateSubject,
        isAiRegen,
        initPPT,
        regenIndex,
        transferIdParams,
        isFromHuaweiCustom,
        getCurrentScene,
        isFullscreen,
        showCreateTip,
        hideCreateTip,
        fetchAIPPTComponentList,
        getStagePoints,
        replaceComponentKit,
    }) => {
        const [showTools, setShowTools] = useState(false);
        const setIsClickingStage = useNavigationCollapseStore((state) => state.setIsClickingStage);
        const { handleMultiSelect, handleDeleteTrack, handleAfterCopyOrDel } = useMultiSelect();
        const { $t } = useLocaleStore();
        const isAiPPT = checkTypeIsAiPPT(posterType);
        const isPPT = checkTypeIsPPT(canvasType) || isAiPPT;
        const extraInfo = editor.services.frame.getExtraInfo(stageIndex);
        const isCover = checkIsCover(extraInfo.settingType);

        const [hoverIndex, setHoverIndex] = useState(-1);
        const isCurrent = activeIndex === stageIndex;
        const count = editor.services.frame.getFrameIdsLength();
        const [showTool, setShowTool] = useState(false);
        const toolRef = useClickOutside(() => {
            setShowTool(false);
        });

        const [size, setSize] = useState(editor.services.frame.getSizeByIndex(stageIndex));
        const { width, height } = size;
        const previewWrapScale = getScale(CONTENT_SIZE, width, height).scale;

        const isSelected = selectedIndexes.includes(stageIndex);
        const [dragIndex, setDragIndex] = useState(-1);

        useEffect(() => {
            // 监听导航栏滚动事件
            events.on(NAV_SCROLL_EVENT, () => {
                setShowTools(false);
            });
            return () => {
                events.off(NAV_SCROLL_EVENT);
            };
        }, []);

        // 正在被拖拽的舞台索引，-1 表示结束拖拽
        useEffect(() => {
            events.on(IS_DRAGGING_EVENT, (dragIndex: number) => {
                setDragIndex(dragIndex);
            });
            return () => {
                events.off(IS_DRAGGING_EVENT);
            };
        }, []);

        useEffect(() => {
            const handler = () => {
                setShowTools(false);
            };
            // 监听导航栏滚动事件
            const scrollHandler = events.on(NAV_SCROLL_FRAME_EVENT, handler);
            document.addEventListener('mousedown', handler);
            return () => {
                document.removeEventListener('mousedown', handler);
                events.off(scrollHandler);
            };
        }, []);

        useEffect(() => {
            // 舞台快捷操作按钮点击事件
            const trackQuickOperateHandle = (type: string) => {
                setShowTools(false);
            };
            const quickClickHandler = editor
                .getInstance<QuickStageOperate>('QuickStageOperate')
                .onQuickClick(trackQuickOperateHandle);
            return () => {
                editor.off(quickClickHandler);
            };
        }, []);

        const handleContextMenu = (e: React.MouseEvent) => {
            e.preventDefault();
            setShowTools(false);
        };

        return (
            <Draggable key={originIndex} draggableId={`${originIndex}`} index={stageIndex}>
                {(provided) => (
                    <div
                        className={classnames('c-navigation-collapse__item', {
                            active: isCurrent || isSelected,
                            current: isCurrent,
                            selected: isSelected,
                            isDragging: dragIndex === stageIndex,
                            multiDragging: dragIndex === stageIndex && selectedIndexes.length > 1,
                        })}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onMouseEnter={() => setHoverIndex(stageIndex)}
                        onMouseLeave={() => setHoverIndex(-1)}
                        onContextMenu={(e) => handleContextMenu(e)}
                        onClick={(e) => {
                            handleMultiSelect(stageIndex, e);
                        }}
                    >
                        <div
                            className={classnames('c-navigation-collapse__item-wrap')}
                            style={{ minHeight: height * previewWrapScale }}
                        >
                            <div className="c-navigation-collapse__item-index">{stageIndex + 1}</div>
                            {stageExtraInfo?.aippt_template_replace_v2 ? (
                                <AIPPTComponent
                                    editor={editor}
                                    fetchAIPPTComponentList={fetchAIPPTComponentList}
                                    getStagePoints={getStagePoints}
                                    replaceComponentKit={replaceComponentKit}
                                    templateExtraInfo={stageExtraInfo}
                                    onClick={() => {
                                        if (activeIndex !== stageIndex) {
                                            closeAiPPTPrompt();
                                            editor.enableOperate();
                                        }
                                        editor.services.frame.delActiveLayer();
                                        editor.services.frame.setPickActiveFrame(true);
                                        setIsClickingStage(true);
                                        editor.services.frame.setActiveIndex(stageIndex);
                                        editor.services.frame.scrollToActiveFrame();
                                        // todo
                                        // triggerNavFuncClickEvent();
                                    }}
                                    activeIndex={activeIndex}
                                    index={stageIndex}
                                />
                            ) : null}
                            <Popover
                                placement="bottomLeft"
                                content={
                                    <div className="c-navigation-collapse__item-tools-ul">
                                        {/* 添加页面 */}
                                        <div
                                            className="c-navigation-collapse__item-tools-li"
                                            onMouseDown={(e) => {
                                                // e.stopPropagation();
                                                setShowTools(false);
                                                if (editor.services.frame.getFrameIdsLength() < MAX_STAGES) {
                                                    if (isPPT) {
                                                        const stage =
                                                            editor.services.frame.getLayersByIndex(stageIndex);

                                                        editor.services.stage.create(
                                                            stageIndex,
                                                            undefined,
                                                            stage[0],
                                                            stageExtraInfo?.masterPage?.layers,
                                                        );
                                                    } else {
                                                        editor.services.stage.create(stageIndex);
                                                    }
                                                    // 添加页面后选中新页面
                                                    handleAfterCopyOrDel();
                                                    // todo
                                                    // triggerNavFuncClickEvent();
                                                    window._stat_track('hb_edit_navigation_click', {
                                                        class: 'add_page',
                                                    });
                                                    window._stat_track('tpbj_subfunction_click', {
                                                        func: '19_91',
                                                    });
                                                    window._stat_track('tpbj_subfunction_tick', {
                                                        func: '19_91',
                                                    });
                                                } else {
                                                    Message.warning(MAX_STAGES_TIP());
                                                }
                                            }}
                                        >
                                            {$t((v) => v.collapse.navigationCollapse.addPage)}
                                        </div>
                                        {/* 复制页面 */}
                                        <div
                                            className="c-navigation-collapse__item-tools-li"
                                            onMouseDown={(e) => {
                                                // e.stopPropagation();
                                                setShowTools(false);
                                                if (editor.services.frame.getFrameIdsLength() < MAX_STAGES) {
                                                    editor.services.stage.clone(stageIndex);
                                                    // 复制页面后选中新页面
                                                    handleAfterCopyOrDel();
                                                    // todo
                                                    // triggerNavFuncClickEvent();
                                                    window._stat_track('hb_edit_navigation_click', {
                                                        class: 'copy_page',
                                                    });
                                                    window._stat_track('tpbj_subfunction_click', {
                                                        func: '19_92',
                                                    });
                                                    window._stat_track('tpbj_subfunction_tick', {
                                                        func: '19_92',
                                                    });
                                                } else {
                                                    Message.warning(MAX_STAGES_TIP());
                                                }
                                            }}
                                        >
                                            {$t((v) => v.collapse.navigationCollapse.copyPage)}
                                        </div>
                                        {/* 删除页面 */}
                                        {count > 1 ? (
                                            <div
                                                className="c-navigation-collapse__item-tools-li"
                                                onMouseDown={(e) => {
                                                    // e.stopPropagation();
                                                    setShowTools(false);
                                                    handleDeleteTrack();
                                                    editor.services.stage.delete(stageIndex);
                                                    editor.services.frame.scrollToActiveFrame();
                                                    // todo
                                                    // triggerNavFuncClickEvent();
                                                    window._stat_track('hb_edit_navigation_click', {
                                                        class: 'delete_page',
                                                    });
                                                    handleAfterCopyOrDel();
                                                }}
                                            >
                                                {$t((v) => v.collapse.navigationCollapse.deletePage)}
                                            </div>
                                        ) : null}
                                    </div>
                                }
                                overlayClassName="c-navigation-collapse__item-tools-popover"
                                open={showTools}
                            >
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowTools(!showTools);
                                    }}
                                    className="c-navigation-collapse__item-tools"
                                    style={{
                                        display: showTools ? 'flex' : 'none',
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                    >
                                        <path
                                            d="M2.375 5C1.82272 5 1.375 5.44772 1.375 6C1.375 6.55228 1.82272 7 2.375 7C2.92728 7 3.375 6.55228 3.375 6C3.375 5.44772 2.92728 5 2.375 5Z"
                                            fill="white"
                                        />
                                        <path
                                            d="M6 5C5.44772 5 5 5.44772 5 6C5 6.55228 5.44772 7 6 7C6.55228 7 7 6.55228 7 6C7 5.44772 6.55228 5 6 5Z"
                                            fill="white"
                                        />
                                        <path
                                            d="M9.625 5C9.07272 5 8.625 5.44772 8.625 6C8.625 6.55228 9.07272 7 9.625 7C10.1773 7 10.625 6.55228 10.625 6C10.625 5.44772 10.1773 5 9.625 5Z"
                                            fill="white"
                                        />
                                    </svg>
                                </div>
                            </Popover>
                            <div
                                className={classnames('c-navigation-collapse__item-preview')}
                                onClick={(e) => {
                                    // e.stopPropagation();
                                    if (activeIndex !== stageIndex) {
                                        closeAiPPTPrompt();
                                        editor.enableOperate();
                                    }
                                    editor.services.frame.delActiveLayer();
                                    editor.services.frame.setPickActiveFrame(true);
                                    setIsClickingStage(true);
                                    editor.services.frame.scrollToActiveFrame();
                                    // todo
                                    // triggerNavFuncClickEvent();
                                }}
                            >
                                <div
                                    className="c-navigation-collapse__item-content"
                                    style={{
                                        width,
                                        height,
                                        borderRadius: Math.ceil((4 / CONTENT_SIZE) * width),
                                        transform: `translate(-50%, -50%) scale(${previewWrapScale})`,
                                    }}
                                >
                                    <RenderStagePreview
                                        editor={editor}
                                        events={events}
                                        originIndex={originIndex}
                                        width={width}
                                        height={height}
                                        onSizeChange={setSize}
                                    />
                                </div>
                            </div>
                            {!isCover && isAiPPT ? (
                                <RegenAiPPT
                                    editor={editor}
                                    events={events}
                                    isAiPPT={isAiPPT}
                                    isAiRegen={isAiRegen}
                                    initPPT={initPPT}
                                    regenIndex={regenIndex}
                                    transferIdParams={transferIdParams}
                                    isFromHuaweiCustom={isFromHuaweiCustom}
                                    getCurrentScene={getCurrentScene}
                                    isFullscreen={isFullscreen}
                                    showCreateTip={showCreateTip}
                                    hideCreateTip={hideCreateTip}
                                    transferTemplateToClient={transferTemplateToClient}
                                    packageTemplateConf={packageTemplateConf}
                                    fetchPPTApplyItem={fetchPPTApplyItem}
                                    fetchPPTCheckItemProcess={fetchPPTCheckItemProcess}
                                    fetchPPTItemDetail={fetchPPTItemDetail}
                                    templateSubject={templateSubject}
                                    index={stageIndex}
                                    hoverIndex={hoverIndex}
                                    recordId={recordId}
                                    setIsAiCreate={setIsAiCreate}
                                    setAiRegen={setAiRegen}
                                />
                            ) : null}
                            {dragIndex === stageIndex && selectedIndexes.length > 1 ? (
                                <i className="multi-dragging-border"></i>
                            ) : null}
                        </div>
                        <div
                            ref={toolRef}
                            className="c-navigation-collapse__item-add"
                            style={{
                                opacity: showTool ? 1 : 0,
                            }}
                            onClick={(e) => {
                                setShowTool(!showTool);
                            }}
                        >
                            <span
                                className="c-navigation-collapse__item-add-icon"
                                onClick={() => {
                                    if (!showTool) return;
                                    if (editor.services.frame.getFrameIdsLength() >= MAX_STAGES) {
                                        Message.warning(MAX_STAGES_TIP());
                                        return;
                                    }

                                    if (isPPT) {
                                        const stage = editor.services.frame.getLayersByIndex(stageIndex);
                                        editor.services.stage.create(
                                            stageIndex,
                                            undefined,
                                            stage[0],
                                            stageExtraInfo?.masterPage?.layers,
                                        );
                                    } else {
                                        editor.services.stage.create(stageIndex);
                                    }
                                    // 添加页面后选中新页面
                                    handleAfterCopyOrDel();
                                    // todo
                                    // triggerNavFuncClickEvent();

                                    // @TODO 拷贝的代码, 确认这个地方是否需要埋点
                                    // window._stat_track('hb_edit_navigation_click', {
                                    //     class: 'add_page',
                                    // });
                                    // window._stat_track('tpbj_subfunction_click', {
                                    //     func: '19_91',
                                    // });
                                    // window._stat_track('tpbj_subfunction_tick', {
                                    //     func: '19_91',
                                    // });
                                }}
                            >
                                <IconFont name="PlusBlack" fontSize={10} />
                            </span>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    },
);
